/**
 * TrendIntro — Editorial introduction section
 *
 * DESIGN (mirrors imagio PageIntro):
 *   Left column: theme pill badges + collapsible body text + "Read more/less" toggle
 *   Right column: floating quote card with decorative opening quote mark
 *   IntersectionObserver triggers entrance animation once on scroll-into-view
 */

import { Chip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./TrendIntro.css";

interface TrendIntroProps {
  /** Optional heading shown above the theme pills / body text (e.g. "Unique Collaboration") */
  title?: string;
  themes: string[];
  paragraphs: string[];
  quote: string;
  readMoreLabel?: string;
  readLessLabel?: string;
}

export const TrendIntro = ({
  title,
  themes,
  paragraphs,
  quote,
  readMoreLabel = "Read more",
  readLessLabel = "Read less",
}: TrendIntroProps) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => {
      const collapsedMax = 144;
      setOverflows(el.scrollHeight > collapsedMax);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [paragraphs]);

  return (
    <div
      ref={sectionRef}
      className={`trend-intro${visible ? " trend-intro--visible" : ""}`}
    >
      <div className="trend-intro__body">
        {/* Left column */}
        <div className="trend-intro__left">
          {title && <h2 className="trend-intro__title">{title}</h2>}
          {themes.length > 0 && (
            <div className="trend-intro__themes">
              {themes.map((theme) => (
                <Chip
                  key={theme}
                  label={theme}
                  size="small"
                  className="trend-intro__theme-chip"
                />
              ))}
            </div>
          )}

          <div
            ref={textRef}
            className={`trend-intro__text-wrap${expanded ? " trend-intro__text-wrap--expanded" : ""}`}
          >
            {paragraphs.map((para, i) => (
              <p key={i} className="trend-intro__paragraph">
                {para}
              </p>
            ))}
            {!expanded && overflows && (
              <div className="trend-intro__fade" aria-hidden="true" />
            )}
          </div>

          {overflows && (
            <button
              type="button"
              className="trend-intro__toggle"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? readLessLabel : readMoreLabel}
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
                className={`trend-intro__toggle-arrow ${expanded ? "trend-intro__toggle-arrow--expanded" : "trend-intro__toggle-arrow--collapsed"}`}
              >
                <path
                  d="M6 4L10 8L6 12"
                  stroke="#009FE4"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Right column — quote card */}
        <div className="trend-intro__quote">
          <span className="trend-intro__quote-mark" aria-hidden="true">
            "
          </span>
          <p className="trend-intro__quote-text">{quote}</p>
          <div className="trend-intro__quote-accent" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};
